"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

import { Image as SectionImage } from "@src/types/section";

import ContentfulImage from "./image";

const CROSSFADE_INTERVAL = 5000;

/**
 * Renders the full-bleed background for a Section.
 * - Single image: static fill with a slow zoom while in view.
 * - Multiple images: opacity crossfade on an interval.
 * - prefers-reduced-motion: no zoom, no auto-advance (first image only).
 */
const SectionBackground = ({ images }: { images: SectionImage[] }) => {
	const reduceMotion = useReducedMotion();
	const ref = useRef(null);
	const isInView = useInView(ref, { margin: "-10%" });
	const [index, setIndex] = useState(0);

	const multiple = images.length > 1;

	useEffect(() => {
		if (reduceMotion !== false || !multiple || !isInView) return;
		const id = setInterval(() => setIndex((i) => (i + 1) % images.length), CROSSFADE_INTERVAL);
		return () => clearInterval(id);
	}, [reduceMotion, multiple, isInView, images.length]);

	return (
		<motion.div
			ref={ref}
			className="absolute inset-0 overflow-hidden"
			initial={false}
			animate={isInView && !reduceMotion && !multiple ? { scale: 1.06 } : { scale: 1 }}
			transition={{ duration: 6, ease: "easeOut" }}
		>
			{images.map((img, i) => (
				<motion.div
					key={img.url}
					className="absolute inset-0"
					initial={false}
					animate={{ opacity: multiple ? (i === index ? 1 : 0) : 1 }}
					transition={{ duration: 1.2, ease: "easeInOut" }}
				>
					<ContentfulImage
						className="object-cover"
						src={img.url ?? ""}
						alt={img.title ?? img.description ?? ""}
						fill
						quality={90}
						loading="lazy"
						sizes="100vw"
					/>
				</motion.div>
			))}
		</motion.div>
	);
};

export default SectionBackground;
